import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useBeforeUnload } from 'react-use'
import { v4 as uuidv4 } from 'uuid'
import { Box, IconButton, Typography } from '@mui/material'
import { Button, Icon } from 'src/components/atoms'
import BreadcrumbsPipe, { Path } from 'src/components/molecules/BreadcrumbsPipe'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import Modal from 'src/components/molecules/Modal'
import { EditReadItem } from 'src/slices/editCalculations/editCalculationsSlice'
import { CalculationItem, CalculationDSL } from 'src/slices/services/api'

import { isEqual } from 'src/utils/array'
import { trimer } from 'src/utils/string'
import { Cell as BodyCell } from '../components/EditReadItemBodyRow'
import EditReadItemTable, { BodyRow } from '../components/EditReadItemTable'
import EditReadItemFunctions, { editReadItemCell } from './function'
import style from './styles.module.scss'

type FieldValues = {
  rows: string[][]
}

type Result = {
  [index: number]: string[]
}

export type EditReadItemProps = {
  breadcrumbs: Path[]
  calculationItems?: CalculationItem[]
  editReadItems: EditReadItem[]
  dsls?: CalculationDSL[]
  onUpdateEditReadItem: (editReadItem: EditReadItem[]) => void
  onClickGoForward: (updatedEditReadItem: EditReadItem[]) => void
  onClickConfirmDiscard: () => void
}

const EditReadItemTemplate: React.StyledFC<EditReadItemProps> = ({
  breadcrumbs,
  calculationItems,
  editReadItems,
  dsls,
  onUpdateEditReadItem,
  onClickGoForward,
  onClickConfirmDiscard,
}: EditReadItemProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [initialValues, setInitialValues] = useState<string[]>([])
  const [rows, setRows] = useState<BodyRow[]>(EditReadItemFunctions.rows([]))
  const [isDefault, setIsDefault] = useState(false)
  const [showModal, setShowModal] = useState(false)
  // NOTE: 初期の header column から、いくつ cell を増やすかを管理
  const [addedCellCount, setAddedCellCount] = useState(0)
  const formMethods = useForm<FieldValues>()

  const defaultValuesWithIndex: Result = useMemo(() => {
    let result: Result = {}
    if (editReadItems.length) {
      result = editReadItems.reduce<Result>((accumulator, current) => {
        const { calculationIndex } = current
        const number = calculationIndex + 1
        return {
          ...accumulator,
          [number]: [...(accumulator[number] ?? []), current.readItem],
        }
      }, {})
    }
    return result
  }, [editReadItems])

  const setDefaultValues = useCallback(() => {
    const lengths = Object.values(defaultValuesWithIndex).map(
      (value) => value.length
    )
    setAddedCellCount(lengths.length > 0 ? Math.max(...lengths) - 1 : 0)
    setRows((prevRows) => {
      return prevRows.map((row, rowIndex) => {
        const defaultValues = defaultValuesWithIndex[Number(row.number)]
        if (!defaultValues) return row

        const updatedCells: BodyCell[] = defaultValues.map(
          (defaultValue, cellIndex) => {
            const name = EditReadItemFunctions.getInputCellName(
              rowIndex,
              cellIndex
            )
            return {
              ...editReadItemCell(name),
              defaultValue,
            }
          }
        )
        return {
          ...row,
          cells: updatedCells,
        }
      })
    })
  }, [defaultValuesWithIndex])

  useEffect(() => {
    if (!isLoading && rows && formMethods.getValues().rows) {
      setInitialValues(Object.values(formMethods.getValues().rows.flat()))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formMethods は useForm の戻り値で参照が安定しているため除外
  }, [isLoading, rows])

  // calculationItemsの長さを追跡して、変更時のみrowsを更新
  const prevCalculationItemsLengthRef = useRef<number>(0)
  useEffect(() => {
    if (!calculationItems) {
      prevCalculationItemsLengthRef.current = 0
      return
    }
    if (prevCalculationItemsLengthRef.current !== calculationItems.length) {
      prevCalculationItemsLengthRef.current = calculationItems.length
      setRows(EditReadItemFunctions.rows(calculationItems))
      setIsLoading(false)
    }
  }, [calculationItems])

  useEffect(() => {
    // dsls があり、かつ初期値に ocrFormat がある場合はそれを入力値として保存する
    if (
      dsls?.length &&
      dsls.some((dsl) => dsl.ocrFormats) &&
      editReadItems.length === 0
    ) {
      const restoredEditReadItems = EditReadItemFunctions.convertDSLToForm(dsls)
      onUpdateEditReadItem(restoredEditReadItems)
    }
  }, [dsls, editReadItems.length, onUpdateEditReadItem])

  useEffect(() => {
    if (rows.length && editReadItems.length && !isDefault) {
      setIsDefault(true)
      setDefaultValues()
    }
  }, [editReadItems.length, isDefault, rows.length, setDefaultValues])

  // utils
  const getEditReadItems = (): EditReadItem[] => {
    if (!calculationItems) {
      return []
    }
    return formMethods
      .getValues()
      .rows.flatMap((values: string[], rowIndex: number) => {
        if (rowIndex >= calculationItems.length) return []
        return values.map((value: string) => {
          const calculationId = calculationItems[rowIndex].id
          return {
            id: uuidv4(),
            readItem: trimer(value),
            calculationId,
            calculationIndex: rowIndex,
          }
        })
      })
      .filter((item: EditReadItem) => Boolean(item.readItem)) // 空の値は除外
  }

  const handleClickGoForward = () => {
    const updatedEditReadItem = getEditReadItems()
    onClickGoForward(updatedEditReadItem)
  }

  useBeforeUnload(() => {
    const { rows: formRows } = formMethods.getValues()
    if (!formRows) return false
    const currentValues = Object.values(formRows.flat())
    return !isEqual(initialValues, currentValues)
    // NOTE: 第2引数はブラウザのページ離脱確認ダイアログを表示するために必要（カスタムメッセージは表示されず、ブラウザ固有の定型文が表示される）
  }, 'Alert')

  // event handler
  const handleClickClose = () => {
    setShowModal(true)
  }

  const handleClickAdd = (rowIndex: number) => {
    const { cells } = rows[rowIndex]
    if (cells.length - 1 === addedCellCount) {
      setAddedCellCount(addedCellCount + 1)
    }
    const name = EditReadItemFunctions.getInputCellName(rowIndex, cells.length)
    const updatedRow: BodyRow = {
      ...rows[rowIndex],
      cells: [...cells, editReadItemCell(name)],
    }
    setRows(rows.map((row, i) => (i === rowIndex ? updatedRow : row)))
  }

  const handleClickCancel = () => {
    setShowModal(false)
  }

  const renderContent = () => {
    return (
      <Box className={style.content}>
        <Typography className={style.title} variant="h1">
          {`売上報告フォーマットの各項目に対応する\nレシートの項目を登録しましょう。`}
        </Typography>
        <EditReadItemTable
          className={style.table}
          columns={EditReadItemFunctions.columns(addedCellCount)}
          rows={rows}
          onClickAdd={handleClickAdd}
        />
        <Box className={style.buttonWrapper}>
          <Button
            title="つぎへ"
            onClick={handleClickGoForward}
            color="primary"
            size="medium"
          />
        </Box>
      </Box>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form>
        <Box className={style.root}>
          <Box className={style.header} component="header">
            <BreadcrumbsPipe className={style.breadcrumb} paths={breadcrumbs} />
            <Box className={style.closeButtonWrapper}>
              <IconButton
                className={style.closeButton}
                onClick={handleClickClose}
                size="large"
              >
                <Icon icon="close" />
              </IconButton>
            </Box>
          </Box>
          <Box className={style.contentWrapper}>
            {isLoading ? <CenteringLoading /> : renderContent()}
            <Modal // `disableBackdropClick` is removed by codemod.
              // You can find more details about this breaking change in [the migration guide](https://mui.com/guides/migration-v4/#modal)

              open={showModal}
              title={`今中断するとこれまでの設定内容は\n失われてしまいますがよろしいですか？`}
              agreeButtonTitle="このまま中断する"
              disagreeButtonTitle="設定に戻る"
              onClickAgree={onClickConfirmDiscard}
              onClickDisagree={handleClickCancel}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}

export default EditReadItemTemplate
